import React, { createContext, useContext, useEffect, useState, useMemo } from "react";

/**
 * Lightweight i18n provider — no dependencies.
 *
 * Strategy: English text is the lookup key. If a translation exists in the active
 * language dictionary it's returned, otherwise we fall back to the original key
 * (so untranslated strings stay readable instead of showing `[missing.key]`).
 *
 * Adding a new translated string later is a one-line dictionary entry — no code
 * changes anywhere else.
 */

const SUPPORTED = ["en", "nl"];

// Shared dictionary: key = English source text, value = translation per language.
// Only Dutch is tracked here for now; English just returns the key unchanged.
const DICT = {
  nl: {
    // ─── tagline + brand ──────────────────────────────────────────────
    "Stretch your music muscles": "Rek je muziekspieren uit",
    "Octave · Built for teachers, loved by musicians.": "Octave · Gemaakt voor docenten, geliefd door muzikanten.",

    // ─── Auth pages ───────────────────────────────────────────────────
    "Welcome back": "Welkom terug",
    "Sign in to your studio.": "Log in op je studio.",
    "Email": "E-mail",
    "Password": "Wachtwoord",
    "Sign in": "Inloggen",
    "Signing in...": "Bezig met inloggen...",
    "New here?": "Nieuw hier?",
    "Create an account →": "Account aanmaken →",
    "Let's get you set up": "Laten we je instellen",
    "I'm a teacher": "Ik ben docent",
    "I'm a solo learner": "Ik leer alleen",
    "Join with an invite code": "Doe mee met een uitnodigingscode",
    "Already have an account?": "Heb je al een account?",
    "Have a teacher's invite code?": "Heb je een uitnodigingscode van je docent?",
    "Join their studio": "Doe mee met hun studio",
    "Start practicing": "Begin met oefenen",
    "Start your studio": "Start je studio",
    "Start free 30-day trial": "Start gratis 30-dagen proefperiode",
    "Create studio": "Studio aanmaken",
    "Free 30-day trial. No credit card required.": "Gratis 30-dagen proefperiode. Geen creditcard nodig.",
    "Your name": "Jouw naam",
    "Creating...": "Bezig met aanmaken...",
    "← Back": "← Terug",
    "You were invited!": "Je bent uitgenodigd!",
    "will be applied. When you subscribe, your friend gets a free month too.": "wordt toegepast. Als je je abonneert krijgt je vriend(in) ook een gratis maand.",
    "Referral code": "Verwijscode",

    // ─── Drawer / nav labels ──────────────────────────────────────────
    "Dashboard": "Dashboard",
    "My Curriculum": "Mijn lesplan",
    "Students": "Studenten",
    "Calendar": "Kalender",
    "Resource Hub": "Hulpbronnen",
    "Recitals": "Voordrachten",
    "Invoices": "Facturen",
    "My Music": "Mijn muziek",
    "My Scales": "Mijn toonladders",
    "My Instruments": "Mijn instrumenten",
    "Invite & earn": "Uitnodigen & verdienen",
    "Find a Teacher": "Zoek een docent",
    "Songs": "Liedjes",
    "Scales": "Toonladders",
    "Curriculum": "Lesplan",
    "Profile": "Profiel",
    "Sign out": "Uitloggen",
    "Subscribe": "Abonneren",
    "Menu": "Menu",
    "Theme": "Thema",
    "Language": "Taal",

    // ─── Subscribe page ───────────────────────────────────────────────
    "Subscribe to Octave": "Abonneer je op Octave",
    "Your trial has ended — subscribe to continue.": "Je proefperiode is afgelopen — abonneer je om door te gaan.",
    "Your subscription is active.": "Je abonnement is actief.",
    "Auto-renews monthly. Cancel anytime — you'll keep access until period end.":
      "Vernieuwt automatisch maandelijks. Altijd opzegbaar — je behoudt toegang tot het einde van de periode.",
    "Manage billing": "Facturatie beheren",
    "Cancel at period end": "Opzeggen aan het einde van de periode",
    "Reactivate": "Heractiveren",
    "Renews automatically on": "Vernieuwt automatisch op",
    "Upgrade to Premium": "Upgrade naar Premium",
    "Renew Premium": "Premium verlengen",
    "Octave Base": "Octave Basis",
    "Octave Premium": "Octave Premium",
    "Subscription active": "Abonnement actief",
    "Back to Dashboard": "Terug naar dashboard",
    "Try again": "Opnieuw proberen",
    "Payment received — finalizing…": "Betaling ontvangen — afronden…",

    // ─── Common buttons ───────────────────────────────────────────────
    "Save": "Opslaan",
    "Cancel": "Annuleren",
    "Edit": "Bewerken",
    "Delete": "Verwijderen",
    "Add": "Toevoegen",
    "Loading…": "Laden…",
    "View details →": "Details bekijken →",
    "Copy": "Kopiëren",
    "Share invite": "Uitnodiging delen",
    "Copy link": "Link kopiëren",
    "Search": "Zoeken",
    "Confirm": "Bevestigen",

    // ─── Demo strip ───────────────────────────────────────────────────
    "See demo below": "Bekijk demo hieronder",
    "See what Octave does": "Bekijk wat Octave doet",
    "A quick tour — each tile is a glimpse of a real screen inside the app.":
      "Een korte rondleiding — elke tegel is een blik op een echt scherm in de app.",
    "Smart calendar & lesson punch-card": "Slimme kalender & oefenkaart",
    "Trophies, streaks & shareable milestones": "Trofeeën, reeksen & deelbare mijlpalen",
    "Songs & scales, on every instrument": "Liedjes & toonladders, op elk instrument",
    "Recurring invoicing — set it, forget it": "Terugkerende facturatie — instellen en vergeten",
    "Plan recitals, broadcast updates": "Plan voordrachten, deel updates",
    "Earn a free month per friend": "Verdien een gratis maand per vriend(in)",
    "Start free trial →": "Start gratis proefperiode →",
    "I already have an account": "Ik heb al een account",
    "Ready to stretch your music muscles?": "Klaar om je muziekspieren uit te rekken?",
    "Auto-populated month + day view. Students stamp every practice session right from their dashboard.":
      "Automatisch gevulde maand- en dagweergave. Studenten stempelen elke oefensessie direct vanaf hun dashboard.",
    "Mastered scales and songs unlock zooming-trophy celebrations + a one-tap shareable image.":
      "Geleerde toonladders en liedjes ontgrendelen trofeevieringen en een deelbare afbeelding met één tik.",
    "Track progress across every instrument in the world — one library, per-instrument tabs.":
      "Volg je vooruitgang op elk instrument ter wereld — één bibliotheek, tabbladen per instrument.",
    "Weekly, biweekly, or monthly. We backfill late invoices automatically.":
      "Wekelijks, tweewekelijks of maandelijks. We vullen achterstallige facturen automatisch aan.",
    "Multi-venue photos, sponsors, and one-tap messaging to every attendee.":
      "Foto's van meerdere locaties, sponsors en eenmalig berichten naar elke aanwezige.",
    "Every friend who subscribes adds 30 days to your plan. No cap.":
      "Elke vriend(in) die zich abonneert voegt 30 dagen toe aan je plan. Geen limiet.",

    // ─── Teacher Dashboard ────────────────────────────────────────────
    "Welcome back, maestro": "Welkom terug, maestro",
    "Hello,": "Hallo,",
    "trial days left.": "proefdagen over.",
    "Your studio is active.": "Je studio is actief.",
    "Your trial has ended.": "Je proefperiode is afgelopen.",
    "+ Add Student": "+ Student toevoegen",
    "Weekly revenue": "Wekelijkse omzet",
    "Monthly revenue": "Maandelijkse omzet",
    "Today's lessons": "Lessen van vandaag",
    "Open calendar →": "Open kalender →",
    "No lessons today. Enjoy the quiet!": "Geen lessen vandaag. Geniet van de rust!",
    "Open →": "Openen →",
    "Invite students": "Studenten uitnodigen",
    "Share this link with students or parents to join your studio.":
      "Deel deze link met studenten of ouders om aan je studio mee te doen.",
    "Invite code:": "Uitnodigingscode:",
    "My students": "Mijn studenten",
    "View all →": "Alles bekijken →",
    "Add your first student to get started.": "Voeg je eerste student toe om te beginnen.",
    "Copied!": "Gekopieerd!",

    // ─── Student Dashboard ────────────────────────────────────────────
    "You've got this!": "Je kunt het!",
    "Hello": "Hallo",
    "Log practice": "Oefening loggen",
    "Songs I'm learning": "Liedjes die ik leer",
    "Songs I'm Learning": "Liedjes die ik leer",
    "My Streaks": "Mijn reeksen",
    "day streak": "dagen reeks",
    "Full session": "Volledige sessie",
    "Total min": "Totaal min",
    "Tap to see your pieces and status.": "Tik om je stukken en status te zien.",
    "12 major scales, one happy keyboard.": "12 majeur-toonladders, één blij klavier.",
    "See your lessons and log practice here.": "Zie hier je lessen en log je oefeningen.",
    "Next Recital": "Volgende voordracht",
    "No recital scheduled yet.": "Nog geen voordracht gepland.",
    "My Instrument": "Mijn instrument",
    "Show off your instrument (and your dream one).":
      "Laat je instrument zien (en je droominstrument).",

    // ─── Songs page ───────────────────────────────────────────────────
    "Track what you're working on, now, next and always.":
      "Volg waar je nu, daarna en altijd aan werkt.",
    "Add song": "Liedje toevoegen",
    "Add an instrument to start tracking songs.": "Voeg een instrument toe om liedjes te volgen.",
    "Pop over to": "Ga naar",
    "and tap \"Add instrument\" to get started.": "en tik op \"Instrument toevoegen\" om te beginnen.",
    "New song": "Nieuw liedje",
    "Title *": "Titel *",
    "Title required": "Titel vereist",
    "Composer": "Componist",
    "Status": "Status",
    "Song added": "Liedje toegevoegd",
    "Delete song?": "Liedje verwijderen?",
    "No songs yet. Add your first piece!": "Nog geen liedjes. Voeg je eerste stuk toe!",

    // ─── Scales page ──────────────────────────────────────────────────
    "Add an instrument to start tracking scales.": "Voeg een instrument toe om toonladders te volgen.",
    "Head to": "Ga naar",
    "and tap \"Add instrument\" to get started": "en tik op \"Instrument toevoegen\" om te beginnen",
    "pick a key, tap a status to log your progress, the keyboard below lights up the notes.":
      "kies een toonsoort, tik op een status om je vooruitgang te loggen — het klavier hieronder licht de noten op.",
    "major — your progress": "majeur — jouw vooruitgang",
    "Tap to update": "Tik om te wijzigen",
    "Not started": "Niet gestart",

    // ─── Calendar page ────────────────────────────────────────────────
    "Today": "Vandaag",
    "Month": "Maand",
    "Day": "Dag",
    "Week": "Week",
    "Lessons": "Lessen",
    "Lesson": "Les",
    "minutes": "minuten",
    "Log a session": "Sessie loggen",
    "Saved!": "Opgeslagen!",
    "Tap a day to log practice.": "Tik op een dag om je oefening te loggen.",
    "Your lessons, auto-populated from each student's schedule.":
      "Je lessen, automatisch ingevuld vanuit het rooster van elke student.",

    // ─── Recitals page ────────────────────────────────────────────────
    "Plan recitals": "Voordrachten plannen",
    "Schedule, track attendees, and broadcast updates.": "Plannen, aanwezigen volgen en updates uitsturen.",
    "Plan performances, invite students, share venue photos, recognise sponsors, message your attendees.":
      "Plan optredens, nodig studenten uit, deel locatiefoto's, erken sponsors en stuur berichten naar je aanwezigen.",
    "Plan next recital": "Volgende voordracht plannen",
    "No recitals scheduled. Plan one to get started.":
      "Geen voordrachten gepland. Plan er één om te beginnen.",
    "+ New recital": "+ Nieuwe voordracht",
    "Title": "Titel",
    "Date": "Datum",
    "Time": "Tijd",
    "Venue": "Locatie",
    "Notes": "Notities",
    "No recitals planned.": "Geen voordrachten gepland.",
    "Broadcast": "Versturen",
    "Send a message to all attendees.": "Stuur een bericht naar alle aanwezigen.",
    "Message": "Bericht",
    "Send": "Versturen",

    // ─── Invoices page ────────────────────────────────────────────────
    "Invoice": "Factuur",
    "Invoice created": "Factuur aangemaakt",
    "Track payments received and outstanding.": "Volg ontvangen en openstaande betalingen.",
    "New invoice": "Nieuwe factuur",
    "Student *": "Student *",
    "Pick a student": "Kies een student",
    "Amount (USD) *": "Bedrag (USD) *",
    "Description": "Omschrijving",
    "Due date": "Vervaldatum",
    "Student and amount required": "Student en bedrag vereist",
    "Delete invoice?": "Factuur verwijderen?",
    "Outstanding": "Openstaand",
    "Received": "Ontvangen",
    "Count": "Aantal",
    "Paid": "Betaald",
    "Mark paid": "Markeer als betaald",

    // ─── Resource Hub ─────────────────────────────────────────────────
    "City pricing, your public teacher listing, and your custom student fields.":
      "Stadprijzen, je openbare docentvermelding en je eigen studentvelden.",
    "City pricing": "Stadprijzen",
    "Public listing": "Openbare vermelding",
    "Custom fields": "Aangepaste velden",
    "City": "Stad",
    "Avg 30-min ($)": "Gem. 30 min ($)",
    "Avg 60-min ($)": "Gem. 60 min ($)",
    "Build my curriculum": "Mijn lesplan opbouwen",
    "Plan your weekly focus points. Students see their progress on the Curriculum tab in their dashboard.":
      "Plan je wekelijkse focuspunten. Studenten zien hun voortgang op het tabblad Lesplan in hun dashboard.",
    "Add week": "Week toevoegen",
    "Add focus point": "Focuspunt toevoegen",

    // ─── My Instruments page ──────────────────────────────────────────
    "Pick the instruments you play. Tap one to set a current and a dream model.":
      "Kies de instrumenten die je bespeelt. Tik op één om een huidig en een droommodel in te stellen.",
    "Add instrument": "Instrument toevoegen",
    "Your roster. Tap a fighter to see stats, edit gear, or manage progress.":
      "Jouw roster. Tik op een vechter om statistieken te zien, gear te bewerken of voortgang te beheren.",
    "Remove": "Verwijderen",
    "My current": "Mijn huidige",
    "My dream": "Mijn droom",

    // ─── Find a Teacher / Marketplace ─────────────────────────────────
    "Browse teachers near you": "Bekijk docenten in je buurt",
    "Filter by city, instrument, or rate.": "Filter op stad, instrument of tarief.",
    "Contact": "Contact",
    "All instruments": "Alle instrumenten",
    "All cities": "Alle steden",
    "No teachers match your filters yet.": "Nog geen docenten gevonden met deze filters.",

    // ─── Referrals page ───────────────────────────────────────────────
    "Invite musicians. Earn free months.": "Nodig muzikanten uit. Verdien gratis maanden.",
    "Give a month, get a month": "Geef een maand, krijg een maand",
    "Every friend who signs up with your link and subscribes earns you 30 free days on your own plan. Unlimited referrals, no cap.":
      "Elke vriend(in) die zich met jouw link aanmeldt en abonneert verdient je 30 gratis dagen op je eigen plan. Onbeperkte verwijzingen, geen limiet.",
    "Friends who converted": "Vrienden die converteerden",
    "they finished a paid checkout": "zij hebben een betaalde checkout voltooid",
    "Bonus days earned": "Bonusdagen verdiend",
    "free months so far": "gratis maanden tot nu toe",
    "Reward per conversion": "Beloning per conversie",
    "added straight to your plan": "direct aan je plan toegevoegd",
    "Your invite link": "Jouw uitnodigingslink",
    "Or share your code:": "Of deel je code:",
    "Share your link or code with other musicians.":
      "Deel je link of code met andere muzikanten.",
    "They sign up for a free trial.": "Zij melden zich aan voor een gratis proefperiode.",
    "When they subscribe, you get 30 free days added to your plan — no cap, invite as many as you want.":
      "Als zij zich abonneren, krijg je 30 gratis dagen toegevoegd aan je plan — geen limiet, nodig uit zoveel als je wilt.",
    "How it works": "Hoe het werkt",
    "Invite link copied": "Uitnodigingslink gekopieerd",

    // ─── Students page ────────────────────────────────────────────────
    "All students": "Alle studenten",
    "+ Add student": "+ Student toevoegen",
    "Loading students…": "Studenten laden…",
    "No students yet.": "Nog geen studenten.",
    "Manage your studio roster.": "Beheer je studio-overzicht.",
    "Add Student": "Student toevoegen",
    "No students yet. Add your first one!": "Nog geen studenten. Voeg je eerste toe!",
    "RECITAL": "VOORDRACHT",
    "-min at": "-min om",
    "days/wk": "dagen/wk",
    "-min sessions": "-min sessies",

    // ─── Student Form ────────────────────────────────────────────────
    "Edit student": "Student bewerken",
    "New student": "Nieuwe student",
    "Contact info": "Contactgegevens",
    "Name *": "Naam *",
    "Student email": "E-mail student",
    "Student phone": "Telefoon student",
    "Emergency contact name": "Naam noodcontact",
    "Emergency phone": "Telefoon noodcontact",
    "Parent name": "Naam ouder",
    "Parent phone": "Telefoon ouder",
    "Parent email": "E-mail ouder",
    "Lesson settings": "Lesinstellingen",
    "Length of session": "Duur van de sessie",
    "Frequency setting": "Frequentie-instelling",
    "Tap bubbles to select lesson days.": "Tik op bolletjes om lesdagen te kiezen.",
    "Time of day": "Tijd van de dag",
    "Lessons end date (optional)": "Einddatum lessen (optioneel)",
    "Daily practice goal (minutes)": "Dagelijks oefendoel (minuten)",
    "Student will be in recital": "Student doet mee aan voordracht",
    "Recurring billing": "Terugkerende facturering",
    "Invoices auto-generate on the due date.": "Facturen worden automatisch aangemaakt op de vervaldag.",
    "Billing cadence": "Factureringsfrequentie",
    "No recurring billing": "Geen terugkerende facturering",
    "Weekly": "Wekelijks",
    "Every 2 weeks": "Elke 2 weken",
    "Monthly": "Maandelijks",
    "Amount per invoice (USD)": "Bedrag per factuur (USD)",
    "Description on invoice": "Omschrijving op factuur",
    "Start date (first invoice)": "Startdatum (eerste factuur)",
    "Leave blank to start today. Past dates will backfill missed invoices.":
      "Laat leeg om vandaag te starten. Met datums in het verleden vul je gemiste facturen aan.",
    "Saving...": "Opslaan...",
    "Save changes": "Wijzigingen opslaan",
    "Add student": "Student toevoegen",
    "Delete this student?": "Deze student verwijderen?",
    "Deleted": "Verwijderd",
    "Student updated": "Student bijgewerkt",
    "Student added": "Student toegevoegd",
    "Save failed": "Opslaan mislukt",
    "Back": "Terug",
    "e.g. 120": "bijv. 120",
    "e.g. Weekly piano lessons": "bijv. Wekelijkse pianolessen",

    // ─── Student detail ──────────────────────────────────────────────
    "← Back to students": "← Terug naar studenten",
    "Daily Streak": "Dagelijkse reeks",
    "Full Session Streak": "Volledige sessies reeks",
    "Total Sessions": "Totaal sessies",
    "Total Minutes": "Totaal minuten",
    "Session:": "Sessie:",
    "Days:": "Dagen:",
    "End date:": "Einddatum:",
    "Daily goal:": "Dagelijks doel:",
    "Recital:": "Voordracht:",
    "Yes": "Ja",
    "No": "Nee",
    "Billing:": "Facturering:",
    "No recurring billing set": "Geen terugkerende facturering ingesteld",
    "Email:": "E-mail:",
    "Phone:": "Telefoon:",
    "Parent:": "Ouder:",
    "Emergency:": "Noodcontact:",
    "Songs (this student)": "Liedjes (deze student)",
    "No songs yet.": "Nog geen liedjes.",
    "Curriculum progress": "Voortgang lesplan",
    "Week complete ✓": "Week voltooid ✓",
    "goals": "doelen",
    "next": "volgende",
    "— next": "— volgende",

    // ─── Calendar details ────────────────────────────────────────────
    "Practice punch card": "Oefen-stempelkaart",
    "✓ Logged today — keep it up!": "✓ Vandaag gelogd — ga zo door!",
    "No practice logged yet for this day.": "Nog geen oefening voor deze dag gelogd.",
    "Events": "Gebeurtenissen",
    "Nothing scheduled.": "Niets gepland.",
    "Log your practice": "Log je oefening",
    "I did a full session": "Ik deed een volledige sessie",
    "I did a half session": "Ik deed een halve sessie",
    "Log this session": "Deze sessie loggen",
    "Awesome job, keep going!": "Super gedaan, ga zo door!",
    "Back to calendar": "Terug naar kalender",
    "+ more": "+ meer",

    // ─── Songs extras ────────────────────────────────────────────────
    "Notes": "Notities",

    // ─── Recitals extras ─────────────────────────────────────────────
    "Plan a recital": "Plan een voordracht",
    "Message (optional)": "Bericht (optioneel)",
    "Duration (min)": "Duur (min)",
    "Location": "Locatie",
    "Venue photos": "Locatiefoto's",
    "Add photo": "Foto toevoegen",
    "No photos yet — show off the hall!": "Nog geen foto's — laat de zaal zien!",
    "Remove venue photo": "Locatiefoto verwijderen",
    "Sponsors": "Sponsors",
    "Add sponsor": "Sponsor toevoegen",
    "Thank the local brands supporting the event.":
      "Bedank de lokale merken die het evenement steunen.",
    "Sponsor name": "Naam sponsor",
    "Logo": "Logo",
    "Attendees": "Aanwezigen",
    "Use toggled students": "Aangevinkte studenten gebruiken",
    "Pick manually": "Handmatig kiezen",
    "Highlight the students attending recital and then hit \"Add students\" button.":
      "Markeer de studenten die naar de voordracht komen en klik dan op \"Studenten toevoegen\".",
    "Add all toggled": "Alle aangevinkte toevoegen",
    "Add selected": "Geselecteerden toevoegen",
    "Save recital": "Voordracht opslaan",
    "at": "om",
    "Sponsor": "Sponsor",
    "Sponsored by": "Gesponsord door",
    "Messages to attendees": "Berichten aan aanwezigen",
    "attendees": "aanwezigen",
    "Send message": "Bericht verzenden",
    "Message attendees": "Aanwezigen berichten",
    "Sending to": "Verzenden naar",
    "of": "van",
    "Dress code is formal. Please arrive by 5:30 for warm-ups!":
      "Kledingvoorschrift is formeel. Kom graag om 17:30 voor het inspelen!",
    "Title and date required": "Titel en datum vereist",
    "Recital scheduled": "Voordracht gepland",
    "Delete recital?": "Voordracht verwijderen?",
    "Write a message first": "Schrijf eerst een bericht",
    "Added": "Toegevoegd",
    "toggled students": "aangevinkte studenten",
    "students": "studenten",
    "Message sent to": "Bericht verzonden naar",

    // ─── Invoices extras ─────────────────────────────────────────────
    "Amount": "Bedrag",
    "Due": "Vervalt",
    "Unpaid": "Onbetaald",

    // ─── Resource Hub extras ─────────────────────────────────────────
    "Add a week": "Week toevoegen",
    "Week #": "Week #",
    "Focus points": "Focuspunten",
    "Save week": "Week opslaan",
    "Title required": "Titel vereist",
    "Week added": "Week toegevoegd",
    "Week": "Week",
    "Studio prices": "Studio-prijzen",
    "Driving to student": "Rijden naar student",
    "State": "Staat",
    "Studio 30-min": "Studio 30 min",
    "Studio 60-min": "Studio 60 min",
    "At-Student 30-min": "Bij student 30 min",
    "At-Student 60-min": "Bij student 60 min",
    "Saved": "Opgeslagen",
    "Four customizable fields for your own notes, goals, or reminders.":
      "Vier aanpasbare velden voor je eigen notities, doelen of herinneringen.",
    "Field": "Veld",
    "label": "label",
    "Value": "Waarde",
    "Save custom fields": "Aangepaste velden opslaan",
    "Listing published": "Vermelding gepubliceerd",
    "Listing saved (not yet public)": "Vermelding opgeslagen (nog niet openbaar)",
    "Show my studio in \"Find a teacher\" search":
      "Toon mijn studio in \"Zoek een docent\"-resultaten",
    "When turned on, solo learners in your city can discover you and reach out.":
      "Als dit aanstaat kunnen zelfstandige leerlingen in jouw stad je vinden en contact opnemen.",
    "e.g. Nashville": "bijv. Amsterdam",
    "e.g. TN": "bijv. NH",
    "About your studio": "Over je studio",
    "15 years teaching piano and voice. Studio in East Nashville, recitals twice a year.":
      "15 jaar ervaring met piano- en zangles. Studio in Amsterdam-Oost, twee voordrachten per jaar.",
    "Rate — 30 min ($)": "Tarief — 30 min ($)",
    "Rate — 60 min ($)": "Tarief — 60 min ($)",
    "Teaches in studio": "Geeft les in studio",
    "Drives to student": "Rijdt naar student",
    "Contact email": "Contact-e-mail",
    "you@example.com": "jij@voorbeeld.com",
    "Contact phone": "Contacttelefoon",
    "(555) 123-4567": "020 123 4567",
    "Publish listing": "Vermelding publiceren",
    "Save draft": "Concept opslaan",

    // ─── My Instruments extras ───────────────────────────────────────
    "Add an instrument": "Een instrument toevoegen",
    "Pick one to add to your roster. You'll be able to fill in the details next.":
      "Kies er een om aan je roster toe te voegen. Daarna vul je de details in.",
    "Base plan:": "Basis-abonnement:",
    "instruments used.": "instrumenten gebruikt.",
    "for unlimited.": "voor onbeperkt.",
    "You've already added every instrument we support. 🎉":
      "Je hebt elk ondersteund instrument al toegevoegd. 🎉",
    "Trophies": "Trofeeën",
    "Perfected": "Geperfectioneerd",
    "Type": "Type",
    "Finish / color": "Afwerking / kleur",
    "Make / brand": "Merk",
    "Model": "Model",
    "Nickname": "Bijnaam",
    "Dream photo": "Droomfoto",
    "Photo": "Foto",
    "Remove this instrument": "Dit instrument verwijderen",
    "You need at least one instrument.": "Je hebt minstens één instrument nodig.",
    "Removed": "Verwijderd",
    "Couldn't add instrument": "Kon instrument niet toevoegen",
    "No instruments yet. Add your first one to start tracking songs, scales, and trophies.":
      "Nog geen instrumenten. Voeg je eerste toe om liedjes, toonladders en trofeeën te volgen.",
    "Close": "Sluiten",

    // ─── Find Teacher ────────────────────────────────────────────────
    "Find me a teacher": "Zoek een docent voor mij",
    "Browse teachers in your city who are accepting students. Reach out, take a trial lesson, and join their studio when you're ready.":
      "Bekijk docenten in je stad die nieuwe leerlingen aannemen. Neem contact op, volg een proefles en sluit je aan bij hun studio wanneer je er klaar voor bent.",
    "Instrument": "Instrument",
    "Searching...": "Zoeken...",
    "Searching…": "Zoeken…",
    "Enter a city and instrument, then tap": "Voer een stad en instrument in en tik dan op",
    "No teachers yet": "Nog geen docenten",
    "in": "in",
    "for": "voor",
    "Try another city or instrument — our directory grows every week.":
      "Probeer een andere stad of instrument — onze lijst groeit elke week.",
    "In-studio": "In studio",
    "At student's home": "Bij student thuis",
    "Call": "Bellen",
    "/ 30 min": "/ 30 min",
    "/ 60 min": "/ 60 min",

    // ─── Referrals extras ────────────────────────────────────────────
    "Practice with me on Octave 🎼 — grab a free month with my invite:":
      "Oefen met mij op Octave 🎼 — pak een gratis maand met mijn uitnodiging:",
    "Try Octave with me": "Probeer Octave met mij",
    "Copy your invite link:": "Kopieer je uitnodigingslink:",

    // ─── Subscribe extras ────────────────────────────────────────────
    "Keep your studio running": "Houd je studio draaiend",
    "You have": "Je hebt",
    "free trial days left.": "gratis proefdagen over.",
    "Cancel your subscription at the end of this billing period? You'll keep access until then.":
      "Abonnement opzeggen aan het einde van deze factureringsperiode? Je behoudt tot dan toegang.",
    "Subscription will cancel at period end. You can reactivate anytime.":
      "Abonnement wordt opgezegd aan het einde van de periode. Je kunt altijd heractiveren.",
    "Cancel failed": "Opzeggen mislukt",
    "Subscription reactivated.": "Abonnement geheractiveerd.",
    "Reactivate failed": "Heractiveren mislukt",
    "Switch to a teacher account? Your 30-day trial will restart so you can onboard students risk-free.":
      "Overschakelen naar een docentaccount? Je 30-daagse proefperiode start opnieuw zodat je studenten risicoloos kunt onboarden.",
    "You're now a teacher! Fresh 30-day trial started. Add your first student!":
      "Je bent nu docent! Nieuwe 30-daagse proefperiode gestart. Voeg je eerste student toe!",
    "Upgrade failed": "Upgraden mislukt",
    "You already have an active subscription.": "Je hebt al een actief abonnement.",
    "Unable to start checkout": "Kon afrekenen niet starten",
    "Couldn't open billing portal": "Kon facturatieportaal niet openen",
    "Redirecting...": "Doorverwijzen...",
    "/ month": "/ maand",
    "Subscribe for": "Abonneer voor",
    "PREMIUM": "PREMIUM",
    "ACTIVE": "ACTIEF",
    "active": "actief",
    "cancelling": "opzeggen",
    "Access continues until": "Toegang duurt tot",
    "Reactivate anytime.": "Je kunt altijd heractiveren.",
    "end of period": "einde van de periode",
    "Renews on": "Vernieuwt op",
    "Active.": "Actief.",
    "Base plan:": "Basis-abonnement:",
    "Up to": "Tot",
    "unlimited": "onbeperkt",
    "instruments": "instrumenten",
    "· up to": "· tot",
    "Studio Pro": "Studio Pro",
    "STUDIO PRO": "STUDIO PRO",
    "Youth": "Jeugd",
    "Adult": "Volwassen",
    "Conservatory": "Conservatorium",
    "Editorial Jazz": "Editorial Jazz",
    "Blue Ocean": "Blauwe Oceaan",
    "Green Land": "Groen Land",
    "Neutral": "Neutraal",
    "Sunset": "Zonsondergang",
    "Midnight": "Middernacht",
    "Arcade": "Arcade",
    "Pick your look": "Kies je stijl",
    "Try them all — the whole app reskins as you tap. You can change this anytime.":
      "Probeer ze allemaal — de hele app verandert van uiterlijk terwijl je tikt. Je kunt dit altijd aanpassen.",
    "Hello, Avery": "Hallo, Avery",
    "Practice today": "Vandaag oefenen",
    "Skip for now": "Voor nu overslaan",
    "Upgrade": "Upgrade",
    "Today!": "Vandaag!",
    "Use this look": "Gebruik deze stijl",
    "Renew Studio Pro": "Studio Pro verlengen",
    "Upgrade to Studio Pro": "Upgrade naar Studio Pro",
    "Upgrade to Studio Pro to unlock this theme.": "Upgrade naar Studio Pro om dit thema te ontgrendelen.",
    "Custom instruments are a Studio Pro perk.": "Eigen instrumenten zijn een Studio Pro-voordeel.",
    "Upgrade for": "Upgrade voor",
    "to unlock.": "om te ontgrendelen.",
    "Scale beyond 75 students.": "Schaal voorbij 75 studenten.",
    "Unlimited students": "Onbeperkt studenten",
    "(grow past 75 without limits)": "(groei onbeperkt voorbij 75)",
    "Add custom instruments not on the list — your own photo + title":
      "Voeg eigen instrumenten toe die niet in de lijst staan — eigen foto + titel",
    "for Studio Pro perks (unlimited students, custom instruments, themes, background)":
      "voor Studio Pro-extra's (onbeperkt studenten, eigen instrumenten, thema's, achtergrond)",
    "Smart calendar (month & day view) with auto-populated lessons":
      "Slimme kalender (maand & dagweergave) met automatisch ingevulde lessen",
    "Practice streaks, punch cards & rewards":
      "Oefenreeksen, stempelkaarten & beloningen",
    "Songs + Scales tracking per instrument":
      "Liedjes + toonladders volgen per instrument",
    "Recitals, invoicing, resource hub, metrics":
      "Voordrachten, facturatie, hulpbronnen, statistieken",
    "Fun student dashboard + parent access":
      "Leuk studenten-dashboard + oudertoegang",
    "Just": "Slechts",
    "mo total) unlocks:": "/mnd totaal) ontgrendelt:",
    "total) unlocks:": "totaal) ontgrendelt:",
    "Unlimited instruments (base caps at 5)":
      "Onbeperkt instrumenten (basis beperkt tot 5)",
    "Unlimited students (base caps at 75)":
      "Onbeperkt studenten (basis beperkt tot 75)",
    "Bonus app themes (sunset, midnight, arcade…)":
      "Bonus-app-thema's (zonsondergang, middernacht, arcade…)",
    "Upload a custom app background image":
      "Upload een aangepaste app-achtergrond",
    "I'm becoming a teacher and need to add students":
      "Ik word docent en wil studenten toevoegen",
    "Flip your account to a teacher studio. You'll get a":
      "Zet je account om naar een docentstudio. Je krijgt een",
    "fresh 30-day trial": "nieuwe 30-daagse proefperiode",
    "an invite link for your students, and access to calendar, invoicing, curriculum, and recitals. Teacher pricing stays at":
      "een uitnodigingslink voor je studenten en toegang tot kalender, facturatie, lesplan en voordrachten. Docenttarief blijft",
    "up to 75 students.": "tot 75 studenten.",
    "Upgrading...": "Upgraden...",
    "Become a teacher →": "Word docent →",
    "Invite musicians, earn free months": "Nodig muzikanten uit, verdien gratis maanden",
    "Every friend who signs up with your link and subscribes adds":
      "Elke vriend(in) die zich met jouw link aanmeldt en abonneert voegt",
    "30 free days": "30 gratis dagen",
    "to your plan. No cap — invite as many as you like.":
      "aan je plan toe. Geen limiet — nodig er zoveel uit als je wilt.",
    "Get your link →": "Pak je link →",
    "Confirming your payment…": "Je betaling bevestigen…",
    "This usually takes a few seconds.": "Dit duurt meestal enkele seconden.",
    "Thank you! Your account renews automatically every month — manage it anytime from the Subscribe page.":
      "Bedankt! Je account wordt automatisch elke maand vernieuwd — beheer het altijd via de Abonnement-pagina.",
    "Stripe is taking a little longer than usual. Your subscription will activate shortly. You can safely return to your dashboard — we'll update it automatically as soon as Stripe confirms.":
      "Stripe doet er nu wat langer over dan normaal. Je abonnement wordt zo geactiveerd. Je kunt gerust terugkeren naar je dashboard — we werken het automatisch bij zodra Stripe bevestigt.",
    "Payment didn't complete": "Betaling is niet voltooid",
    "Please try again.": "Probeer het opnieuw.",

    // ─── Auth extras ─────────────────────────────────────────────────
    "Welcome back,": "Welkom terug,",
    "Login failed": "Inloggen mislukt",
    "First question — which best describes you?":
      "Eerste vraag — wat omschrijft jou het beste?",
    "Run a studio, add students, invoice, plan recitals. 30-day free trial, then $9.99/mo for up to 75 students with unlimited instruments — and your students use the app free.":
      "Run een studio, voeg studenten toe, factureer en plan voordrachten. 30 dagen gratis proef, daarna $9,99/mnd voor maximaal 75 studenten met onbeperkte instrumenten — en jouw studenten gebruiken de app gratis.",
    "I'm a musician": "Ik ben muzikant",
    "Confirm password": "Bevestig wachtwoord",
    "Passwords don't match.": "Wachtwoorden komen niet overeen.",
    "Forgot your password?": "Wachtwoord vergeten?",
    "Forgot password?": "Wachtwoord vergeten?",
    "← Back to sign in": "← Terug naar inloggen",
    "Back to sign in": "Terug naar inloggen",
    "Enter the email you signed up with and we'll send you a link to reset your password.":
      "Voer het e-mailadres in waarmee je je hebt aangemeld en we sturen je een link om je wachtwoord opnieuw in te stellen.",
    "Send reset link": "Verstuur resetlink",
    "Sending...": "Verzenden...",
    "Check your inbox": "Controleer je inbox",
    "If an account exists for that email, a reset link is on its way. The link is good for 30 minutes.":
      "Als er een account bestaat voor dat e-mailadres, is er een resetlink onderweg. De link is 30 minuten geldig.",
    "Reset password": "Wachtwoord opnieuw instellen",
    "Pick a new password — at least 6 characters.": "Kies een nieuw wachtwoord — minimaal 6 tekens.",
    "New password": "Nieuw wachtwoord",
    "Confirm new password": "Bevestig nieuw wachtwoord",
    "Update password": "Wachtwoord bijwerken",
    "Updating...": "Bijwerken...",
    "Password updated. You can sign in now.": "Wachtwoord bijgewerkt. Je kunt nu inloggen.",
    "Couldn't reset your password. The link may have expired.":
      "Kon je wachtwoord niet opnieuw instellen. De link is mogelijk verlopen.",
    "This reset link is invalid.": "Deze resetlink is ongeldig.",
    "This reset link is invalid or has expired.": "Deze resetlink is ongeldig of verlopen.",
    "Request a new link": "Vraag een nieuwe link aan",
    "All set!": "Helemaal klaar!",
    "Redirecting you to sign in...": "Je wordt doorgestuurd naar inloggen...",
    "Something went wrong. Please try again.": "Er ging iets mis. Probeer het opnieuw.",
    "Coming soon": "Binnenkort",
    "Coming soon to iOS": "Binnenkort beschikbaar voor iOS",
    "Coming soon to Android": "Binnenkort beschikbaar voor Android",
    "Track your own practice, songs, scales, streaks and trophies across every instrument you play — piano, guitar, violin, drums and more. 30-day free trial, then $2.99/mo.":
      "Volg je eigen oefentijd, liedjes, toonladders, reeksen en trofeeën — voor elk instrument dat je speelt: piano, gitaar, viool, drums en meer. 30 dagen gratis proberen, daarna $2,99/mnd.",
    "Track your own practice, songs, scales, streaks and trophies.":
      "Volg je eigen oefening, liedjes, toonladders, reeksen en trofeeën.",
    "30-day free trial, then $2.99/mo.": "30 dagen gratis proef, daarna $2,99/mnd.",
    "Track songs, scales, streaks & trophies — on every instrument you play. 30-day free trial, then $2.99/mo.":
      "Volg liedjes, toonladders, reeksen & trofeeën — op elk instrument dat je bespeelt. 30 dagen gratis proef, daarna $2,99/mnd.",
    "I have an invite code from my teacher":
      "Ik heb een uitnodigingscode van mijn docent",
    "Join your teacher's studio.": "Doe mee met de studio van je docent.",
    "Creating...": "Bezig met aanmaken...",
    "Registration failed": "Registratie mislukt",
    "You're in! Let's make some music. 🎶": "Je bent erbij! Laten we muziek maken. 🎶",
    "Studio created! 30-day free trial active.": "Studio aangemaakt! 30 dagen gratis proef actief.",
    "Join your teacher's studio": "Doe mee met de studio van je docent",
    "Enter the invite code your teacher gave you.":
      "Voer de uitnodigingscode in die je docent je gaf.",
    "Invite code": "Uitnodigingscode",
    "Joining...": "Meedoen...",
    "Welcome to your music journey! 🎶": "Welkom bij je muziekreis! 🎶",
    "Join failed": "Meedoen mislukt",
    "Track every scale, song, streak and trophy — on every instrument you play.":
      "Volg elke toonladder, elk liedje, elke reeks en trofee — op elk instrument dat je bespeelt.",
    "Built for teachers running studios": "Gemaakt voor docenten met een studio",
    "and solo musicians learning on their own. Piano, guitar, violin, drums — one app, every instrument, all your progress in one place.":
      "en muzikanten die alleen leren. Piano, gitaar, viool, drums — één app, elk instrument, al je vooruitgang op één plek.",
    "30-day free trial · Cancel anytime": "30 dagen gratis proef · Altijd opzegbaar",
    "Every instrument in the world — piano, guitar, violin, drums, voice, horns and more. One app, all your progress in one place.":
      "Elk instrument ter wereld — piano, gitaar, viool, drums, zang, blazers en meer. Eén app, al je vooruitgang op één plek.",

    // ─── Student Curriculum ──────────────────────────────────────────
    "You're learning on your own — there's no teacher curriculum yet. If you join a teacher's studio with an invite code, their weeks will show up here.":
      "Je leert alleen — er is nog geen docent-lesplan. Als je je aansluit bij een studio met een uitnodigingscode verschijnen hun weken hier.",
    "Your teacher hasn't published any weeks yet. Check back soon!":
      "Je docent heeft nog geen weken gepubliceerd. Kom snel terug!",
    "Your teacher's plan, one week at a time. Tap each goal once you've nailed it; your teacher sees your progress automatically.":
      "Het lesplan van je docent, week per week. Tik een doel aan zodra je het beheerst; je docent ziet je voortgang automatisch.",
    "goals complete": "doelen voltooid",
    "Mark week complete": "Markeer week als voltooid",
    "Couldn't save — reverting.": "Kon niet opslaan — ongedaan maken.",
    "Loading…": "Laden…",

    // ─── Layout / drawer details ─────────────────────────────────────
    "Metrics + today's lessons": "Statistieken + lessen van vandaag",
    "Plan weekly focus points": "Plan wekelijkse focuspunten",
    "Roster, schedules & progress": "Roster, roosters & voortgang",
    "Month and day view": "Maand- en dagweergave",
    "Pricing, listing, custom fields": "Prijzen, vermelding, aangepaste velden",
    "Plan performances": "Optredens plannen",
    "Paid & outstanding": "Betaald & openstaand",
    "Your own song library": "Je eigen liedjesbibliotheek",
    "Track the 12 major scales": "Volg de 12 majeur-toonladders",
    "Your instruments & dream gear": "Je instrumenten & droomgear",
    "One free month per invite": "Eén gratis maand per uitnodiging",
    "Your musical home": "Je muzikale thuis",
    "Lessons & log practice": "Lessen & oefening loggen",
    "Your teacher's plan": "Het plan van je docent",
    "Pieces & progress": "Stukken & voortgang",
    "Light up the keyboard": "Licht het klavier op",
    "Browse teachers by city": "Docenten per stad bekijken",
    "Student": "Student",
    "Developer": "Ontwikkelaar",
    "Teacher": "Docent",
    "Your trial ends today": "Je proefperiode eindigt vandaag",
    "day left on your free trial": "dag over in je gratis proefperiode",
    "days left on your free trial": "dagen over in je gratis proefperiode",
    "Opening Stripe checkout…": "Stripe-checkout openen…",
    "Tap to upgrade — $9.99/mo, one click →":
      "Tik om te upgraden — $9,99/mnd, één klik →",
    "Opening checkout…": "Checkout openen…",
    "Trial ends today — Upgrade": "Proefperiode eindigt vandaag — Upgrade",
    "day left — Upgrade": "dag over — Upgrade",
    "days left — Upgrade": "dagen over — Upgrade",
    "trial days left": "proefdagen over",
    "Opening…": "Openen…",
    "Developer Panel": "Ontwikkelaarspaneel",
    "Master settings & access log": "Hoofdinstellingen & toegangslog",
    "Subscription": "Abonnement",
    "Manage your plan": "Beheer je abonnement",
    "Activate your plan": "Activeer je abonnement",
    "Custom background": "Aangepaste achtergrond",
    "Saving…": "Opslaan…",
    "Upload image": "Afbeelding uploaden",
    "Background set": "Achtergrond ingesteld",
    "Background cleared": "Achtergrond gewist",
    "Please pick an image under 3 MB.": "Kies een afbeelding kleiner dan 3 MB.",
    "Upload failed": "Upload mislukt",
    "Update failed": "Bijwerken mislukt",
    "Developer Dashboard": "Ontwikkelaars-dashboard",
    "Open menu": "Menu openen",
    "Couldn't start checkout — opening subscribe page.":
      "Kon checkout niet starten — abonnement-pagina openen.",
    "Upgrade to Premium to unlock this theme.":
      "Upgrade naar Premium om dit thema te ontgrendelen.",

    // ─── Celebration / Mastery ───────────────────────────────────────
    "Trophy image saved!": "Trofee-afbeelding opgeslagen!",
    "Now share it on Instagram or Facebook 🎉":
      "Deel het nu op Instagram of Facebook 🎉",
    "Thanks for sharing! 🎉": "Bedankt voor het delen! 🎉",
    "Couldn't generate the image — try again?":
      "Kon de afbeelding niet genereren — opnieuw proberen?",
    "Mastery celebration": "Beheersingsfeestje",
    "MASTERED · MILESTONE UNLOCKED": "BEHEERST · MIJLPAAL ONTGRENDELD",
    "INCREDIBLE!": "ONGELOOFLIJK!",
    "HOORAY!": "HOERA!",
    "Creating…": "Aanmaken…",
    "Share my trophy": "Mijn trofee delen",
    "Keep going! 🎉": "Ga zo door! 🎉",

    // ─── Song status toggle ──────────────────────────────────────────
    "Just started": "Net begonnen",
    "Still working on it": "Nog mee bezig",
    "Almost there": "Bijna zover",
    "Good enough for me": "Goed genoeg voor mij",
    "Perfection": "Perfectie",

    // ─── Piano selector ──────────────────────────────────────────────
    "Spinet": "Spinet",
    "Smallest vertical — great for apartments.":
      "Kleinste piano — ideaal voor appartementen.",
    "Console": "Console",
    "Direct action, enhanced tone, popular home piano.":
      "Direct mechanisme, volle toon, populaire thuispiano.",
    "Studio": "Studio",
    "Workhorse of music schools — durable, great tone.":
      "Werkpaard van muziekscholen — duurzaam, mooie toon.",
    "Full Size / Professional": "Volwaardig / Professioneel",
    "Tallest vertical. Rich tone, built to last generations.":
      "Grootste rechtopstaande piano. Rijke toon, generaties lang mee.",
    "5ft Grand": "1,5m vleugel",
    "5 ft. Smallest grand — apartment- and studio-friendly.":
      "1,5 m. Kleinste vleugel — geschikt voor appartement en studio.",
    "Full Size Grand": "Volwaardige vleugel",
    "5′8″ – 6′2″. The classic living-room grand.":
      "1,75 m – 1,88 m. De klassieke woonkamervleugel.",
    "Semi-Concert Grand": "Semi-concertvleugel",
    "7 ft. Near-concert scale for larger venues and recital halls.":
      "2,1 m. Bijna concertformaat voor grotere zalen en voordrachten.",
    "Concert Grand": "Concertvleugel",
    "9 ft+. The concert-hall standard.":
      "2,75 m+. De standaard voor concertzalen.",
    "I don't have a real piano yet 😥": "Ik heb nog geen echte piano 😥",
    "That's okay — we'll still track your progress.":
      "Dat geeft niks — we volgen je voortgang toch.",

    // ─── Instrument picker ───────────────────────────────────────────
    "Please pick at least one instrument.": "Kies minstens één instrument.",
    "Unlimited-instruments add-on active — track as many as you like.":
      "Uitbreiding onbeperkte instrumenten actief — volg er zoveel als je wilt.",

    // ─── Misc single words used inline ──────────────────────────────
    "min": "min",
    "more": "meer",
    "Log failed": "Loggen mislukt",
    "Saved!": "Opgeslagen!",
    "added to your roster": "toegevoegd aan je roster",
    "from your roster?": "uit je roster?",
    "Songs and scales tied to it will remain, but the instrument won't appear in your tabs.":
      "Liedjes en toonladders die eraan gekoppeld zijn blijven bestaan, maar het instrument verschijnt niet meer in je tabbladen.",
    "has a nickname": "heeft een bijnaam",
    "I play": "die ik bespeel",
    "My dream": "Mijn droom",
    "The": "De",
    "scales mastered": "toonladders beheerst",
    "songs mastered": "liedjes beheerst",
    "Mastered": "Beheerst",
    "Mon": "ma",
    "Tue": "di",
    "Wed": "wo",
    "Thu": "do",
    "Fri": "vr",
    "Sat": "za",
    "Sun": "zo",
    "Invalid email or password": "Ongeldig e-mailadres of wachtwoord",

    // ─── Auth demo strip decorative content ─────────────────────────
    "Sept": "sep",
    "May": "mei",
    "Mastered!": "Beheerst!",
    "scales": "toonladders",
    "songs": "liedjes",
    "streak": "reeks",
    "Invoice": "Factuur",
    "PAID": "BETAALD",
    "Lesson": "Les",
    "Recital fee": "Voordracht-bijdrage",
    "Total": "Totaal",
    "Auto-bills weekly": "Automatisch wekelijks gefactureerd",
    "Spring Recital": "Lente-voordracht",
    "Riverside Hall": "Riverside-zaal",
    "Broadcast: Doors open 6:30pm.": "Bericht: Deuren open om 18:30.",
    "days": "dagen",
    "free month earned": "gratis maand verdiend",
  },
};

const LangCtx = createContext({ lang: "en", setLang: () => {}, t: (s) => s });

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem("octave_lang");
      if (saved && SUPPORTED.includes(saved)) return saved;
    } catch { /* localStorage unavailable */ }
    return "en";
  });

  useEffect(() => {
    try { localStorage.setItem("octave_lang", lang); } catch { /* noop */ }
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = (next) => {
    if (SUPPORTED.includes(next)) setLangState(next);
  };

  const value = useMemo(() => {
    const t = (key) => {
      if (lang === "en") return key;
      return DICT[lang]?.[key] ?? key;
    };
    return { lang, setLang, t, supported: SUPPORTED };
  }, [lang]);

  return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
}

export const useLang = () => useContext(LangCtx);
export const useT    = () => useContext(LangCtx).t;
